export type Locale = 'vi' | 'en';

export const LOCALES: Locale[] = ['vi', 'en'];
export const DEFAULT_LOCALE: Locale = 'vi';

export function getLocale(url: URL): Locale {
  const [, first] = url.pathname.split('/');
  return (LOCALES as string[]).includes(first ?? '') ? (first as Locale) : DEFAULT_LOCALE;
}

export function localePath(locale: Locale, path: string): string {
  if (locale === DEFAULT_LOCALE) return path;
  return `/${locale}${path}`;
}

export function alternatePath(url: URL): string {
  const current = getLocale(url);
  const other = current === 'vi' ? 'en' : 'vi';
  const withoutPrefix = url.pathname.replace(/^\/(vi|en)/, '') || '/';
  return localePath(other, withoutPrefix);
}

/** Pick bilingual field, falling back to vi. */
export function pick<T>(locale: Locale, vi: T, en: T | null | undefined): T {
  return locale === 'en' && en != null ? en : vi;
}

export const labels = {
  vi: {
    classrooms: 'Lớp học',
    events: 'Sự kiện',
    local_spots: 'Địa điểm',
    blog: 'Bài viết',
    resources: 'Tài nguyên',
    search: 'Tìm kiếm',
    filter: 'Lọc',
    past_event: 'Đã diễn ra',
    online: 'Online',
    offline: 'Offline',
    kindergarten: 'Mầm non (3–5t)',
    primary: 'Tiểu học (6–11t)',
    middle: 'THCS (12–15t)',
    above_15: 'Trên 15 tuổi',
    pending: 'Đang chờ duyệt',
    verified: 'Đã xác minh',
    northern: 'Giọng Bắc',
    central: 'Giọng Trung',
    southern: 'Giọng Nam',
    mixed: 'Nhiều giọng',
    individual: 'Cá nhân',
    non_profit: 'Tổ chức phi lợi nhuận',
    government_or_school: 'Do chính phủ hoặc trường chính khóa tổ chức',
    private_language_school: 'Trường dạy tiếng tư nhân',
    community_organized: 'Cộng đồng tự tổ chức',
    free: 'Miễn phí',
    paid: 'Có phí',
    vn_government: 'Tập huấn theo chính phủ VN',
    host_country_government: 'Tập huấn theo chính phủ nước sở tại',
    overseas_vietnamese: 'Tự biên soạn (người Việt hải ngoại)',
    vietnamese_teacher: 'Tự biên soạn (giáo viên Việt Nam)',
    choose_country: 'Chọn quốc gia của bạn',
    choose_country_sub: 'Chúng tôi sẽ hiển thị các lớp học, sự kiện và địa điểm gần bạn.',
    global: 'Toàn cầu',
    global_desc: 'Nội dung dành cho mọi quốc gia',
    featured_in: 'Nổi bật ở',
    see_all: 'Xem tất cả',
    coming_soon: 'Sắp ra mắt',
    coming_soon_body: 'Chúng tôi đang xây dựng phần này. Hãy quay lại sớm nhé!',
    back_to_country: 'Quay lại trang quốc gia',
    classrooms_desc: 'Lớp học tiếng Việt cho con bạn',
    events_desc: 'Sự kiện cộng đồng người Việt',
    local_spots_desc: 'Địa điểm thân thiện với gia đình',
    blog_desc: 'Bài viết và câu chuyện cộng đồng',
    resources_desc: 'Tài liệu hữu ích cho phụ huynh',
    type_video: 'Video',
    type_audio: 'Âm thanh',
    type_book: 'Sách & Ebook',
    type_worksheet: 'Bài tập & In ấn',
    type_app: 'Ứng dụng',
    type_other: 'Khác',
    age_0_5: '0–5 (Mầm non)',
    age_6_11: '6–11 (Tiểu học)',
    age_12_15: '12–15 (THCS)',
    age_15_plus: 'Trên 15 tuổi',
    age_parents: 'Phụ huynh & Nhà giáo',
    content_language: 'Ngôn ngữ',
    content_culture: 'Văn hoá & Di sản',
    content_stories: 'Câu chuyện & Cổ tích',
    content_identity: 'Tư duy & Bản sắc',
    access_free: 'Miễn phí',
    access_freemium: 'Freemium',
    access_paid: 'Có phí',
    filter_type: 'Loại',
    filter_age_group: 'Độ tuổi',
    filter_content: 'Nội dung',
    filter_access: 'Truy cập',
    filter_accent: 'Phương ngữ',
    no_resources: 'Không tìm thấy tài nguyên nào.',
    visit_resource: 'Truy cập tài nguyên',
    read_more: 'Đọc thêm',
    go_to_website: 'Đến trang web',
    submit_tip: 'Gửi gợi ý',
    tip_page_title: 'Chia sẻ gợi ý của bạn',
    tip_page_intro:
      'Bạn biết một lớp tiếng Việt, một cuốn sách hay, một sự kiện hoặc bất kỳ điều gì hữu ích cho các gia đình Việt? Hãy mách cho ban biên tập — chúng tôi sẽ kiểm duyệt và chia sẻ miễn phí với các phụ huynh khác. Một cái tên thôi cũng quý, kèm địa chỉ hoặc đường link thì càng tốt.',
    tip_field_tip: 'Gợi ý của bạn',
    tip_field_tip_ph: 'Hãy cho chúng tôi biết đó là gì, ở đâu, và vì sao bạn thấy nó hữu ích…',
    tip_field_name: 'Tên của bạn',
    tip_field_email: 'Email',
    tip_field_consent:
      'Tôi đồng ý cho ban biên tập sử dụng thông tin này để xử lý gợi ý của tôi.',
    tip_submit: 'Gửi gợi ý',
    tip_submitting: 'Đang gửi…',
    tip_success_title: 'Cảm ơn bạn!',
    tip_success_body: 'Chúng tôi đã nhận được gợi ý của bạn và sẽ xem xét sớm.',
    tip_err_required: 'Vui lòng nhập nội dung gợi ý.',
    tip_err_name: 'Vui lòng nhập tên của bạn.',
    tip_err_email: 'Vui lòng nhập email hợp lệ.',
    tip_err_consent: 'Vui lòng đồng ý để chúng tôi xử lý gợi ý của bạn.',
    tip_err_failed: 'Không gửi được. Vui lòng thử lại.',
    report_button: 'Báo cáo',
    report_page_title: 'Báo cáo nội dung',
    report_page_intro:
      'Nếu nội dung này có gì đó không phù hợp, sai lệch hoặc là spam, hãy cho ban biên tập biết. Chúng tôi sẽ xem xét và xử lý sớm.',
    report_field_content: 'Lý do báo cáo',
    report_field_content_ph: 'Hãy cho chúng tôi biết vấn đề bạn gặp với nội dung này…',
    report_field_name: 'Tên của bạn',
    report_field_email: 'Email',
    report_submit: 'Gửi báo cáo',
    report_submitting: 'Đang gửi…',
    report_success_title: 'Cảm ơn bạn!',
    report_success_body: 'Chúng tôi đã nhận được báo cáo của bạn và sẽ xem xét sớm.',
    report_err_required: 'Vui lòng nhập lý do báo cáo.',
    report_err_name: 'Vui lòng nhập tên của bạn.',
    report_err_email: 'Vui lòng nhập email hợp lệ.',
    report_err_failed: 'Không gửi được. Vui lòng thử lại.',
    report_err_limit: 'Bạn đã gửi quá nhiều báo cáo hôm nay. Vui lòng thử lại vào ngày mai.',
    report_err_no_target: 'Không tìm thấy nội dung cần báo cáo.',
  },
  en: {
    classrooms: 'Classrooms',
    events: 'Events',
    local_spots: 'Local spots',
    blog: 'Blog',
    resources: 'Resources',
    search: 'Search',
    filter: 'Filter',
    past_event: 'Past event',
    online: 'Online',
    offline: 'Offline',
    kindergarten: 'Kindergarten (3–5y)',
    primary: 'Primary school (6–11y)',
    middle: 'Middle school (12–15y)',
    above_15: 'Ages 15+',
    pending: 'Pending',
    verified: 'Verified',
    northern: 'Northern',
    central: 'Central',
    southern: 'Southern',
    mixed: 'Mixed dialects',
    individual: 'Individual',
    non_profit: 'Non-profit organisation',
    government_or_school: 'Government or formal school',
    private_language_school: 'Private language school',
    community_organized: 'Community-organised',
    free: 'Free',
    paid: 'Paid',
    vn_government: 'Trained by Vietnamese government',
    host_country_government: 'Trained by host country government',
    overseas_vietnamese: 'Self-developed (overseas Vietnamese)',
    vietnamese_teacher: 'Self-developed (Vietnamese teacher)',
    choose_country: 'Choose your country',
    choose_country_sub: 'We will show classrooms, events, and local spots near you.',
    global: 'Global',
    global_desc: 'Content for everyone, everywhere',
    featured_in: 'Featured in',
    see_all: 'See all',
    coming_soon: 'Coming soon',
    coming_soon_body: 'We are still building this section. Check back soon!',
    back_to_country: 'Back to country page',
    classrooms_desc: 'Vietnamese classes for your children',
    events_desc: 'Vietnamese community events',
    local_spots_desc: 'Family-friendly places to visit',
    blog_desc: 'Articles and community stories',
    resources_desc: 'Helpful resources for parents',
    type_video: 'Video',
    type_audio: 'Audio',
    type_book: 'Book & Ebook',
    type_worksheet: 'Worksheet & Printable',
    type_app: 'App',
    type_other: 'Other',
    age_0_5: '0–5 (Pre-school)',
    age_6_11: '6–11 (Primary)',
    age_12_15: '12–15 (Secondary)',
    age_15_plus: '15 and above',
    age_parents: 'Parents & Educators',
    content_language: 'Language',
    content_culture: 'Culture & Heritage',
    content_stories: 'Stories & Folklore',
    content_identity: 'Mindset & Identity',
    access_free: 'Free',
    access_freemium: 'Freemium',
    access_paid: 'Paid',
    filter_type: 'Type',
    filter_age_group: 'Age group',
    filter_content: 'Content',
    filter_access: 'Access',
    filter_accent: 'Accent',
    no_resources: 'No resources found.',
    visit_resource: 'Visit resource',
    read_more: 'Read more',
    go_to_website: 'Go to website',
    submit_tip: 'Submit a tip',
    tip_page_title: 'Share your tip with us',
    tip_page_intro:
      'Know a Vietnamese class, a great book, an event, or anything else helpful for Vietnamese families? Send it to our editors — we will review it and share it with other parents for free. A name alone is great; an address or link is even better.',
    tip_field_tip: 'Your tip',
    tip_field_tip_ph: 'Tell us what it is, where it is, and why you find it helpful…',
    tip_field_name: 'Your name',
    tip_field_email: 'Email',
    tip_field_consent:
      'I allow the editors to use this information to process my tip.',
    tip_submit: 'Submit tip',
    tip_submitting: 'Sending…',
    tip_success_title: 'Thank you!',
    tip_success_body: 'We have received your tip and will review it soon.',
    tip_err_required: 'Please enter your tip.',
    tip_err_name: 'Please enter your name.',
    tip_err_email: 'Please enter a valid email.',
    tip_err_consent: 'Please give consent so we can process your tip.',
    tip_err_failed: 'Could not submit. Please try again.',
    report_button: 'Report',
    report_page_title: 'Report content',
    report_page_intro:
      'If something about this content is inappropriate, inaccurate, or spam, let our editors know. We will review it and act on it soon.',
    report_field_content: 'Reason for reporting',
    report_field_content_ph: 'Tell us what the problem is with this content…',
    report_field_name: 'Your name',
    report_field_email: 'Email',
    report_submit: 'Submit report',
    report_submitting: 'Sending…',
    report_success_title: 'Thank you!',
    report_success_body: 'We have received your report and will review it soon.',
    report_err_required: 'Please enter a reason for reporting.',
    report_err_name: 'Please enter your name.',
    report_err_email: 'Please enter a valid email.',
    report_err_failed: 'Could not submit. Please try again.',
    report_err_limit: 'You have sent too many reports today. Please try again tomorrow.',
    report_err_no_target: 'Could not find the content to report.',
  },
} satisfies Record<Locale, Record<string, string>>;

export function t(locale: Locale, key: keyof (typeof labels)['vi']): string {
  return labels[locale][key] ?? labels['vi'][key];
}
